import { createContext, ReactNode, useCallback, useContext, useRef } from 'react';
import { GTM, GTMEvents } from 'utils/analytics';

// One discrete buyer action inside checkout (DEV-2120). Fires per action, so the action stream +
// checkout_start + checkout_complete reconstructs the visit without a heavy per-change snapshot.
// Mirrors the yaga-mobile implementation so the web + mobile checkout_interaction streams share
// param shape and values in the same GA4 property.
export type CheckoutInteraction = {
  // what the buyer touched: delivery_option | delivery_location | payment_option | payment_bank |
  // promo_code | wallet_toggle | pay_button | info_toggle | address
  element: string;
  // what they did: select | apply | remove | toggle | tap | fill (fill = completed a manual
  // edit of the location field; fires once per changed value, never carries the typed content)
  action: string;
  // the resulting value, e.g. 'smartpost' | 'apple_pay' | 'SUMMER10' | 'on' | 'HABAEE2X' |
  // 'buyer_protection_banner' / 'buyer_protection_summary' (each info button identifiable by
  // value alone)
  value?: string;
  // generic optional qualifier of value, reusable across elements — e.g. delivery_location carries
  // the provider in value (paxi/pargo) and the specific point code here (P0102). Absent otherwise.
  value_detail?: string;
  // 'first_party' (chosen in our own checkout UI) | 'third_party' (returned from a provider-hosted
  // surface we launched, e.g. the Paxi/Pargo delivery-point map)
  value_source?: 'first_party' | 'third_party';
};

export type TrackCheckoutInteraction = (interaction: CheckoutInteraction) => void;

const CheckoutInteractionContext = createContext<TrackCheckoutInteraction>(() => undefined);

// used by the buy page to build the tracker once (owns the per-visit interaction counter) and share
// it down the checkout tree; pay_button is emitted by the page itself with the same tracker
export const useCheckoutInteractionTracker = (checkoutVisitId: string): TrackCheckoutInteraction => {
  // interaction_index orders the actions within the visit — client timestamps are unreliable for
  // sequence. Counts actions, not displayed states.
  const interactionIndex = useRef(0);

  return useCallback(
    (interaction: CheckoutInteraction) => {
      interactionIndex.current += 1;

      GTM.logEvent(GTMEvents.CHECKOUT_INTERACTION, {
        checkout_visit_id: checkoutVisitId,
        interaction_index: interactionIndex.current,
        ...interaction,
      });
    },
    [checkoutVisitId],
  );
};

export const CheckoutInteractionProvider = ({ track, children }: { track: TrackCheckoutInteraction; children: ReactNode }) => (
  <CheckoutInteractionContext.Provider value={track}>{children}</CheckoutInteractionContext.Provider>
);

// consumed by any checkout descendant to emit an interaction at a genuine user-action point (a no-op
// outside a CheckoutInteractionProvider, so shared banners can call it safely off the buy page)
export const useCheckoutInteraction = (): TrackCheckoutInteraction => useContext(CheckoutInteractionContext);
